/**
 * Translations for operations: planner, projects, time, expenses and
 * inventory. Deep-merged over the base locale files; German uses Swiss
 * conventions (no ß).
 */
export const operationsTranslations = {
  en: {
    notification: {
      request_submitted: {
        title: "New request {{number}}",
        body: "{{description}}",
      },
      request_assigned: {
        title: "You were assigned to request {{number}}",
        body: "{{description}}",
      },
      expense_claim_submitted: {
        title: "Expense claim to pay",
        body: "{{name}} submitted {{amount}}.",
      },
      expense_claim_returned: {
        title: "Expense claim sent back",
        body: "Please correct: {{reason}}",
      },
      expense_claim_paid: {
        title: "Expense claim paid",
        body: "{{amount}} was paid out.",
      },
      timesheet_submitted: {
        title: "Timesheet to approve",
        body: "{{name}} submitted the week of {{week}}.",
      },
      timesheet_approve: {
        title: "Timesheet approved",
        body: "Your week of {{week}} was approved.",
      },
      timesheet_reject: {
        title: "Timesheet returned",
        body: "Your week of {{week}} needs a correction: {{reason}}",
      },
      timesheet_reopen: {
        title: "Timesheet reopened",
        body: "Your week of {{week}} can be edited again.",
      },
      invoice_overdue: {
        title: "Invoice {{number}} is overdue",
        body: "{{customer}} · due {{dueDate}}",
      },
    },
    projectRoles: {
      title: "Project roles",
      subtitle:
        "What managers and members may do on the projects they belong to. Company-wide rights come from the roles above.",
      capability: "Capability",
      capabilities: {
        view: "See the project",
        edit: "Edit project details and status",
        manage_members: "Manage the project team",
        view_planning: "See the planning",
        manage_planning: "Plan assignments",
        view_costs: "See budget and costs",
        manage_costs: "Manage budget and costs",
        link_documents: "Link offers and invoices",
        record_time: "Book working time",
        manage_inventory: "Issue and return material",
      },
    },
    customer: { singular: "Customer" },
    common: { confirm: "Confirm" },
    nav: { week: "My week", approvals: "Approvals", expenses: "Expenses" },
    priority: { low: "Low", normal: "Normal", high: "High" },
    offer: { createProject: "Create project" },
    compensation: {
      laborCostRate: "Internal cost per hour",
      laborCostRateHint:
        "Prices this person's approved project time in project costs, including approved time that has no rate yet.",
    },
    employees: { editPersonal: "Edit my details" },
    planner: {
      filters: "Filters",
      conflict: {
        outside_work_schedule: "The person has no planned work on this date.",
        holiday: "Company holiday",
        absence: "The person is absent on this date.",
        overlapping_assignment:
          "The time overlaps another assignment of this person.",
        over_capacity: "The day would exceed the planned working time.",
      },
      subtitle: "Plan who works on which project and when.",
      allTeams: "All teams",
      allPeople: "Everyone",
      allProjects: "All projects",
      searchProjects: "Search projects",
      dragHint: "Drag a project onto a day, or click it to plan it.",
      goTo: "Go to date",
      assignOn: "Plan {{name}} on {{date}}",
    },
    projects: {
      assigneesHint:
        "Assign the people who work on this project. Managers can edit it and plan the team.",
      credited: "Credited",
      laborCost: "Labour",
      material: "Material",
      members: "Members",
      delete: "Delete project",
      deleteConfirm:
        "Delete this project? Only planned projects without any records can be deleted.",
      fromOffer: "From offer {{number}}",
      statusReason: "Reason",
      costKind: {
        material: "Material",
        subcontractor: "Subcontractor",
        other: "Other",
      },
      documentType: {
        request: "Request",
        offer: "Offer",
        invoice: "Invoice",
      },
      transition: { planned: "Back to planned" },
    },
    time: {
      weekTitle: "My week",
      weekSubtitle: "Check your week and hand it in for approval.",
      weekHelp: "Record time on the clock and assign it to projects:",
      thisWeek: "This week",
      previousWeek: "Previous week",
      nextWeek: "Next week",
      goToWeek: "Go to week",
      submitWeek: "Submit week",
      withdrawWeek: "Withdraw",
      beforeSubmitting: "Before you can submit this week:",
      rejectedBecause: "Returned:",
      overtime: "Overtime",
      vacationLeft: "Vacation left",
      onProjects: "On projects",
      absences: "Absences",
      difference: "Difference",
      approvalsTitle: "Timesheet approvals",
      approvalsSubtitle: "Review submitted weeks and approve or return them.",
      noTimesheets: "No timesheets match these filters.",
      nothingRecorded: "Nothing recorded this week.",
      rejectTitle: "Return timesheet",
      issue: {
        open_stamp: "Clock out first.",
        missing_day: "No work or absence recorded.",
        allocation_mismatch: "Worked time is not fully assigned to projects.",
      },
    },
    expenses: {
      scope: { mine: "My claims", toPay: "To pay", all: "All claims" },
      itemCount_one: "{{count}} item",
      itemCount_other: "{{count}} items",
      receipts: "Receipts",
      receiptsAfterSave: "Add receipt photos after saving the claim.",
      editClaim: "Edit expense claim",
      withdraw: "Withdraw",
      return: "Send back",
      returnTitle: "Send claim back",
      returnReason: "What needs to be corrected?",
      returned: "Needs correction",
      returnedBecause: "Sent back:",
      paidOn: "Paid on {{date}}",
      deleteConfirm: "Delete this draft claim and its receipts?",
    },
    inventory: {
      item: "Item",
      selectItem: "Select item",
      value: "Value",
      lowOnly: "Low stock only",
      intoLocation: "Into location",
      directionIn: "Add to stock",
      directionOut: "Remove from stock",
      op: {
        receipt: "Receive",
        issue: "Issue to project",
        return: "Return from project",
        transfer: "Transfer",
        adjustment: "Correct stock",
      },
      movement: {
        receipt: "Received",
        issue: "Issued",
        return: "Returned",
        transfer_in: "Transfer in",
        transfer_out: "Transfer out",
        adjustment_in: "Correction +",
        adjustment_out: "Correction −",
      },
    },
  },
  de: {
    notification: {
      request_submitted: {
        title: "Neue Anfrage {{number}}",
        body: "{{description}}",
      },
      request_assigned: {
        title: "Ihnen wurde die Anfrage {{number}} zugewiesen",
        body: "{{description}}",
      },
      expense_claim_submitted: {
        title: "Spesen zu bezahlen",
        body: "{{name}} hat {{amount}} eingereicht.",
      },
      expense_claim_returned: {
        title: "Spesen zurückgewiesen",
        body: "Bitte korrigieren: {{reason}}",
      },
      expense_claim_paid: {
        title: "Spesen bezahlt",
        body: "{{amount}} wurden ausbezahlt.",
      },
      timesheet_submitted: {
        title: "Zeiterfassung zu genehmigen",
        body: "{{name}} hat die Woche vom {{week}} eingereicht.",
      },
      timesheet_approve: {
        title: "Zeiterfassung genehmigt",
        body: "Ihre Woche vom {{week}} wurde genehmigt.",
      },
      timesheet_reject: {
        title: "Zeiterfassung zurückgewiesen",
        body: "Ihre Woche vom {{week}} muss korrigiert werden: {{reason}}",
      },
      timesheet_reopen: {
        title: "Zeiterfassung wieder geöffnet",
        body: "Ihre Woche vom {{week}} kann wieder bearbeitet werden.",
      },
      invoice_overdue: {
        title: "Rechnung {{number}} ist überfällig",
        body: "{{customer}} · fällig am {{dueDate}}",
      },
    },
    projectRoles: {
      title: "Projektrollen",
      subtitle:
        "Was Projektleiter und Mitglieder in ihren Projekten dürfen. Firmenweite Rechte kommen aus den Rollen oben.",
      capability: "Berechtigung",
      capabilities: {
        view: "Projekt sehen",
        edit: "Projektangaben und Status bearbeiten",
        manage_members: "Projektteam verwalten",
        view_planning: "Planung sehen",
        manage_planning: "Einsätze planen",
        view_costs: "Budget und Kosten sehen",
        manage_costs: "Budget und Kosten verwalten",
        link_documents: "Offerten und Rechnungen verknüpfen",
        record_time: "Arbeitszeit buchen",
        manage_inventory: "Material ausgeben und zurücknehmen",
      },
    },
    customer: { singular: "Kunde" },
    common: { confirm: "Bestätigen" },
    nav: {
      week: "Meine Woche",
      approvals: "Freigaben",
      expenses: "Spesen",
    },
    priority: { low: "Tief", normal: "Normal", high: "Hoch" },
    offer: { createProject: "Projekt erstellen" },
    compensation: {
      laborCostRate: "Interner Stundensatz",
      laborCostRateHint:
        "Bewertet die freigegebene Projektzeit dieser Person in den Projektkosten – auch bereits freigegebene Zeit ohne Satz.",
    },
    employees: { editPersonal: "Meine Angaben bearbeiten" },
    planner: {
      filters: "Filter",
      conflict: {
        outside_work_schedule:
          "Die Person arbeitet an diesem Tag laut Plan nicht.",
        holiday: "Feiertag",
        absence: "Die Person ist an diesem Tag abwesend.",
        overlapping_assignment:
          "Die Zeit überschneidet sich mit einem anderen Einsatz dieser Person.",
        over_capacity: "Der Tag würde die geplante Arbeitszeit überschreiten.",
      },
      subtitle: "Planen, wer wann an welchem Projekt arbeitet.",
      allTeams: "Alle Teams",
      allPeople: "Alle Personen",
      allProjects: "Alle Projekte",
      searchProjects: "Projekte suchen",
      dragHint:
        "Projekt auf einen Tag ziehen oder anklicken, um es einzuplanen.",
      goTo: "Zu Datum springen",
      assignOn: "{{name}} am {{date}} einplanen",
    },
    projects: {
      assigneesHint:
        "Personen zuweisen, die am Projekt arbeiten. Projektleiter können es bearbeiten und das Team planen.",
      credited: "Gutgeschrieben",
      laborCost: "Arbeit",
      material: "Material",
      members: "Mitglieder",
      delete: "Projekt löschen",
      deleteConfirm:
        "Projekt löschen? Nur geplante Projekte ohne Einträge können gelöscht werden.",
      fromOffer: "Aus Offerte {{number}}",
      statusReason: "Begründung",
      costKind: {
        material: "Material",
        subcontractor: "Subunternehmer",
        other: "Übriges",
      },
      documentType: {
        request: "Anfrage",
        offer: "Offerte",
        invoice: "Rechnung",
      },
      transition: { planned: "Zurück auf geplant" },
    },
    time: {
      weekTitle: "Meine Woche",
      weekSubtitle: "Woche prüfen und zur Freigabe einreichen.",
      weekHelp: "Zeit stempeln und Projekten zuweisen:",
      thisWeek: "Diese Woche",
      previousWeek: "Vorherige Woche",
      nextWeek: "Nächste Woche",
      goToWeek: "Zu Woche springen",
      submitWeek: "Woche einreichen",
      withdrawWeek: "Zurückziehen",
      beforeSubmitting: "Bevor Sie diese Woche einreichen können:",
      rejectedBecause: "Zurückgewiesen:",
      overtime: "Überzeit",
      vacationLeft: "Ferien übrig",
      onProjects: "Auf Projekten",
      absences: "Absenzen",
      difference: "Differenz",
      approvalsTitle: "Zeitfreigaben",
      approvalsSubtitle:
        "Eingereichte Wochen prüfen und freigeben oder zurückweisen.",
      noTimesheets: "Keine Zeiterfassungen für diese Filter.",
      nothingRecorded: "Diese Woche ist nichts erfasst.",
      rejectTitle: "Zeiterfassung zurückweisen",
      issue: {
        open_stamp: "Zuerst ausstempeln.",
        missing_day: "Keine Arbeit oder Absenz erfasst.",
        allocation_mismatch:
          "Die Arbeitszeit ist nicht vollständig Projekten zugewiesen.",
      },
    },
    expenses: {
      scope: {
        mine: "Meine Spesen",
        toPay: "Zu bezahlen",
        all: "Alle Spesen",
      },
      itemCount_one: "{{count}} Position",
      itemCount_other: "{{count}} Positionen",
      receipts: "Belege",
      receiptsAfterSave: "Belegfotos nach dem Speichern hinzufügen.",
      editClaim: "Spesenabrechnung bearbeiten",
      withdraw: "Zurückziehen",
      return: "Zurückweisen",
      returnTitle: "Spesen zurückweisen",
      returnReason: "Was muss korrigiert werden?",
      returned: "Korrektur nötig",
      returnedBecause: "Zurückgewiesen:",
      paidOn: "Bezahlt am {{date}}",
      deleteConfirm: "Diesen Entwurf mit allen Belegen löschen?",
    },
    inventory: {
      item: "Artikel",
      selectItem: "Artikel wählen",
      value: "Wert",
      lowOnly: "Nur tiefer Bestand",
      intoLocation: "Zurück an Lager",
      directionIn: "Bestand erhöhen",
      directionOut: "Bestand verringern",
      op: {
        receipt: "Wareneingang",
        issue: "An Projekt ausgeben",
        return: "Vom Projekt zurücknehmen",
        transfer: "Umbuchen",
        adjustment: "Bestand korrigieren",
      },
      movement: {
        receipt: "Eingang",
        issue: "Ausgabe",
        return: "Rücknahme",
        transfer_in: "Umbuchung ein",
        transfer_out: "Umbuchung aus",
        adjustment_in: "Korrektur +",
        adjustment_out: "Korrektur −",
      },
    },
  },
  fr: {
    notification: {
      request_submitted: {
        title: "Nouvelle demande {{number}}",
        body: "{{description}}",
      },
      request_assigned: {
        title: "La demande {{number}} vous a été attribuée",
        body: "{{description}}",
      },
      expense_claim_submitted: {
        title: "Note de frais à payer",
        body: "{{name}} a soumis {{amount}}.",
      },
      expense_claim_returned: {
        title: "Note de frais renvoyée",
        body: "À corriger : {{reason}}",
      },
      expense_claim_paid: {
        title: "Note de frais payée",
        body: "{{amount}} ont été versés.",
      },
      timesheet_submitted: {
        title: "Feuille de temps à valider",
        body: "{{name}} a soumis la semaine du {{week}}.",
      },
      timesheet_approve: {
        title: "Feuille de temps validée",
        body: "Votre semaine du {{week}} a été validée.",
      },
      timesheet_reject: {
        title: "Feuille de temps renvoyée",
        body: "Votre semaine du {{week}} doit être corrigée : {{reason}}",
      },
      timesheet_reopen: {
        title: "Feuille de temps rouverte",
        body: "Votre semaine du {{week}} peut à nouveau être modifiée.",
      },
      invoice_overdue: {
        title: "La facture {{number}} est échue",
        body: "{{customer}} · échéance le {{dueDate}}",
      },
    },
    projectRoles: {
      title: "Rôles de projet",
      subtitle:
        "Ce que les responsables et les membres peuvent faire dans leurs projets. Les droits généraux viennent des rôles ci-dessus.",
      capability: "Autorisation",
      capabilities: {
        view: "Voir le projet",
        edit: "Modifier le projet et son statut",
        manage_members: "Gérer l'équipe du projet",
        view_planning: "Voir la planification",
        manage_planning: "Planifier les interventions",
        view_costs: "Voir le budget et les coûts",
        manage_costs: "Gérer le budget et les coûts",
        link_documents: "Lier offres et factures",
        record_time: "Saisir le temps de travail",
        manage_inventory: "Sortir et reprendre du matériel",
      },
    },
    customer: { singular: "Client" },
    common: { confirm: "Confirmer" },
    nav: {
      week: "Ma semaine",
      approvals: "Validations",
      expenses: "Frais",
    },
    priority: { low: "Basse", normal: "Normale", high: "Haute" },
    offer: { createProject: "Créer un projet" },
    compensation: {
      laborCostRate: "Coût horaire interne",
      laborCostRateHint:
        "Valorise le temps de projet validé de cette personne, y compris le temps déjà validé sans taux.",
    },
    employees: { editPersonal: "Modifier mes données" },
    planner: {
      filters: "Filtres",
      conflict: {
        outside_work_schedule:
          "La personne ne travaille pas ce jour-là selon son plan.",
        holiday: "Jour férié",
        absence: "La personne est absente ce jour-là.",
        overlapping_assignment:
          "Ce créneau chevauche une autre intervention de cette personne.",
        over_capacity: "La journée dépasserait le temps de travail prévu.",
      },
      subtitle: "Planifier qui travaille sur quel projet et quand.",
      allTeams: "Toutes les équipes",
      allPeople: "Tout le monde",
      allProjects: "Tous les projets",
      searchProjects: "Rechercher des projets",
      dragHint:
        "Glissez un projet sur un jour ou cliquez dessus pour le planifier.",
      goTo: "Aller à la date",
      assignOn: "Planifier {{name}} le {{date}}",
    },
    projects: {
      assigneesHint:
        "Affectez les personnes qui travaillent sur ce projet. Les responsables peuvent le modifier et planifier l’équipe.",
      credited: "Crédité",
      laborCost: "Main-d'œuvre",
      material: "Matériel",
      members: "Membres",
      delete: "Supprimer le projet",
      deleteConfirm:
        "Supprimer ce projet ? Seuls les projets planifiés sans écritures peuvent être supprimés.",
      fromOffer: "Depuis l'offre {{number}}",
      statusReason: "Motif",
      costKind: {
        material: "Matériel",
        subcontractor: "Sous-traitant",
        other: "Autre",
      },
      documentType: {
        request: "Demande",
        offer: "Offre",
        invoice: "Facture",
      },
      transition: { planned: "Revenir à planifié" },
    },
    time: {
      weekTitle: "Ma semaine",
      weekSubtitle: "Vérifiez votre semaine et soumettez-la pour validation.",
      weekHelp: "Pointez le temps et attribuez-le aux projets :",
      thisWeek: "Cette semaine",
      previousWeek: "Semaine précédente",
      nextWeek: "Semaine suivante",
      goToWeek: "Aller à la semaine",
      submitWeek: "Soumettre la semaine",
      withdrawWeek: "Retirer",
      beforeSubmitting: "Avant de pouvoir soumettre cette semaine :",
      rejectedBecause: "Renvoyée :",
      overtime: "Heures supplémentaires",
      vacationLeft: "Vacances restantes",
      onProjects: "Sur projets",
      absences: "Absences",
      difference: "Différence",
      approvalsTitle: "Validation des feuilles de temps",
      approvalsSubtitle:
        "Vérifiez les semaines soumises et validez-les ou renvoyez-les.",
      noTimesheets: "Aucune feuille de temps pour ces filtres.",
      nothingRecorded: "Rien de saisi cette semaine.",
      rejectTitle: "Renvoyer la feuille de temps",
      issue: {
        open_stamp: "Pointez d'abord la sortie.",
        missing_day: "Aucun travail ni absence saisi.",
        allocation_mismatch:
          "Le temps travaillé n'est pas entièrement attribué aux projets.",
      },
    },
    expenses: {
      scope: {
        mine: "Mes frais",
        toPay: "À payer",
        all: "Tous les frais",
      },
      itemCount_one: "{{count}} poste",
      itemCount_other: "{{count}} postes",
      receipts: "Justificatifs",
      receiptsAfterSave:
        "Ajoutez les photos des justificatifs après l'enregistrement.",
      editClaim: "Modifier la note de frais",
      withdraw: "Retirer",
      return: "Renvoyer",
      returnTitle: "Renvoyer la note de frais",
      returnReason: "Que faut-il corriger ?",
      returned: "À corriger",
      returnedBecause: "Renvoyée :",
      paidOn: "Payée le {{date}}",
      deleteConfirm: "Supprimer ce brouillon et ses justificatifs ?",
    },
    inventory: {
      item: "Article",
      selectItem: "Choisir un article",
      value: "Valeur",
      lowOnly: "Stock bas uniquement",
      intoLocation: "Vers l'emplacement",
      directionIn: "Augmenter le stock",
      directionOut: "Diminuer le stock",
      op: {
        receipt: "Réception",
        issue: "Sortie vers projet",
        return: "Retour du projet",
        transfer: "Transfert",
        adjustment: "Corriger le stock",
      },
      movement: {
        receipt: "Réception",
        issue: "Sortie",
        return: "Retour",
        transfer_in: "Transfert entrant",
        transfer_out: "Transfert sortant",
        adjustment_in: "Correction +",
        adjustment_out: "Correction −",
      },
    },
  },
  es: {
    notification: {
      request_submitted: {
        title: "Nueva solicitud {{number}}",
        body: "{{description}}",
      },
      request_assigned: {
        title: "Se te asignó la solicitud {{number}}",
        body: "{{description}}",
      },
      expense_claim_submitted: {
        title: "Nota de gastos por pagar",
        body: "{{name}} envió {{amount}}.",
      },
      expense_claim_returned: {
        title: "Nota de gastos devuelta",
        body: "Corrige por favor: {{reason}}",
      },
      expense_claim_paid: {
        title: "Nota de gastos pagada",
        body: "Se pagaron {{amount}}.",
      },
      timesheet_submitted: {
        title: "Hoja de horas por aprobar",
        body: "{{name}} envió la semana del {{week}}.",
      },
      timesheet_approve: {
        title: "Hoja de horas aprobada",
        body: "Tu semana del {{week}} fue aprobada.",
      },
      timesheet_reject: {
        title: "Hoja de horas devuelta",
        body: "Tu semana del {{week}} requiere una corrección: {{reason}}",
      },
      timesheet_reopen: {
        title: "Hoja de horas reabierta",
        body: "Tu semana del {{week}} se puede editar de nuevo.",
      },
      invoice_overdue: {
        title: "La factura {{number}} está vencida",
        body: "{{customer}} · vencida el {{dueDate}}",
      },
    },
    projectRoles: {
      title: "Roles de proyecto",
      subtitle:
        "Lo que responsables y miembros pueden hacer en sus proyectos. Los derechos generales vienen de los roles de arriba.",
      capability: "Permiso",
      capabilities: {
        view: "Ver el proyecto",
        edit: "Editar datos y estado del proyecto",
        manage_members: "Gestionar el equipo del proyecto",
        view_planning: "Ver la planificación",
        manage_planning: "Planificar asignaciones",
        view_costs: "Ver presupuesto y costes",
        manage_costs: "Gestionar presupuesto y costes",
        link_documents: "Vincular ofertas y facturas",
        record_time: "Registrar tiempo de trabajo",
        manage_inventory: "Entregar y devolver material",
      },
    },
    customer: { singular: "Cliente" },
    common: { confirm: "Confirmar" },
    nav: {
      week: "Mi semana",
      approvals: "Aprobaciones",
      expenses: "Gastos",
    },
    priority: { low: "Baja", normal: "Normal", high: "Alta" },
    offer: { createProject: "Crear proyecto" },
    compensation: {
      laborCostRate: "Coste interno por hora",
      laborCostRateHint:
        "Valora el tiempo de proyecto aprobado de esta persona, incluido el ya aprobado sin tarifa.",
    },
    employees: { editPersonal: "Editar mis datos" },
    planner: {
      filters: "Filtros",
      conflict: {
        outside_work_schedule: "La persona no trabaja ese día según su plan.",
        holiday: "Festivo",
        absence: "La persona está ausente ese día.",
        overlapping_assignment:
          "El horario se solapa con otra asignación de esta persona.",
        over_capacity: "El día superaría el tiempo de trabajo previsto.",
      },
      subtitle: "Planifica quién trabaja en qué proyecto y cuándo.",
      allTeams: "Todos los equipos",
      allPeople: "Todas las personas",
      allProjects: "Todos los proyectos",
      searchProjects: "Buscar proyectos",
      dragHint: "Arrastra un proyecto a un día o haz clic para planificarlo.",
      goTo: "Ir a la fecha",
      assignOn: "Planificar a {{name}} el {{date}}",
    },
    projects: {
      assigneesHint:
        "Asigna a las personas que trabajan en este proyecto. Los responsables pueden editarlo y planificar el equipo.",
      credited: "Abonado",
      laborCost: "Mano de obra",
      material: "Material",
      members: "Miembros",
      delete: "Eliminar proyecto",
      deleteConfirm:
        "¿Eliminar este proyecto? Solo se pueden eliminar proyectos planificados sin registros.",
      fromOffer: "Desde la oferta {{number}}",
      statusReason: "Motivo",
      costKind: {
        material: "Material",
        subcontractor: "Subcontratista",
        other: "Otro",
      },
      documentType: {
        request: "Solicitud",
        offer: "Oferta",
        invoice: "Factura",
      },
      transition: { planned: "Volver a planificado" },
    },
    time: {
      weekTitle: "Mi semana",
      weekSubtitle: "Revisa tu semana y envíala para su aprobación.",
      weekHelp: "Ficha el tiempo y asígnalo a proyectos:",
      thisWeek: "Esta semana",
      previousWeek: "Semana anterior",
      nextWeek: "Semana siguiente",
      goToWeek: "Ir a la semana",
      submitWeek: "Enviar semana",
      withdrawWeek: "Retirar",
      beforeSubmitting: "Antes de poder enviar esta semana:",
      rejectedBecause: "Devuelta:",
      overtime: "Horas extra",
      vacationLeft: "Vacaciones restantes",
      onProjects: "En proyectos",
      absences: "Ausencias",
      difference: "Diferencia",
      approvalsTitle: "Aprobación de horas",
      approvalsSubtitle:
        "Revisa las semanas enviadas y apruébalas o devuélvelas.",
      noTimesheets: "No hay hojas de horas con estos filtros.",
      nothingRecorded: "Nada registrado esta semana.",
      rejectTitle: "Devolver hoja de horas",
      issue: {
        open_stamp: "Primero ficha la salida.",
        missing_day: "No hay trabajo ni ausencia registrados.",
        allocation_mismatch:
          "El tiempo trabajado no está asignado por completo a proyectos.",
      },
    },
    expenses: {
      scope: {
        mine: "Mis gastos",
        toPay: "Por pagar",
        all: "Todos los gastos",
      },
      itemCount_one: "{{count}} partida",
      itemCount_other: "{{count}} partidas",
      receipts: "Recibos",
      receiptsAfterSave: "Añade las fotos de los recibos después de guardar.",
      editClaim: "Editar nota de gastos",
      withdraw: "Retirar",
      return: "Devolver",
      returnTitle: "Devolver nota de gastos",
      returnReason: "¿Qué hay que corregir?",
      returned: "Requiere corrección",
      returnedBecause: "Devuelta:",
      paidOn: "Pagada el {{date}}",
      deleteConfirm: "¿Eliminar este borrador y sus recibos?",
    },
    inventory: {
      item: "Artículo",
      selectItem: "Seleccionar artículo",
      value: "Valor",
      lowOnly: "Solo stock bajo",
      intoLocation: "Hacia la ubicación",
      directionIn: "Aumentar stock",
      directionOut: "Reducir stock",
      op: {
        receipt: "Recepción",
        issue: "Entregar a proyecto",
        return: "Devolución del proyecto",
        transfer: "Traspaso",
        adjustment: "Corregir stock",
      },
      movement: {
        receipt: "Recepción",
        issue: "Entrega",
        return: "Devolución",
        transfer_in: "Traspaso entrada",
        transfer_out: "Traspaso salida",
        adjustment_in: "Corrección +",
        adjustment_out: "Corrección −",
      },
    },
  },
};
